import React, { useState, useRef, useEffect } from 'react';
import { 
  Play, 
  CheckCircle, 
  XCircle, 
  Loader, 
  Image as ImageIcon,
  Upload,
  Trash2,
  Settings,
  Zap,
  Layers,
  Brain,
  Activity,
  RefreshCw,
  Download,
  Eye,
  AlertCircle,
  Clock,
  Target,
  Plus,
  ShieldCheck,
  CheckCircle2,
  Webhook as WebhookIcon
} from 'lucide-react';
import { buildApiUrl, API_ENDPOINTS, API_CONFIG } from '../config/api';
import { useApiKeys } from '../contexts/ApiKeysContext';
import { submitProfileForModeration } from '../api/moderation';
import PageHeader from './PageHeader';
import './TestingPage.css';

interface TestResult {
  success: boolean;
  data?: any;
  error?: string;
  timestamp: string;
}

interface BatchTestResult {
  success: boolean;
  data?: any; // Using any to support both batch face detection and trust score responses
  error?: string;
  timestamp: string;
}

interface ModerationTestProfile {
  id: string;
  profile_id?: string;
  user_id?: string;
  status: 'approved' | 'rejected' | 'pending';
  trust_score: number;
  images: string[];
  bio?: string;
  prompts?: Array<{ question?: string; answer?: string }>;
  metadata?: Record<string, any>;
  image_results?: any[];
  text_results?: { score: number };
  timestamp: string;
  webhook_payload?: any;
}

const TestingPage: React.FC = () => {
  const { apiKeys } = useApiKeys();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selfieImage, setSelfieImage] = useState<string | null>(null);
  const [profileImages, setProfileImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [batchTestResult, setBatchTestResult] = useState<BatchTestResult | null>(null);
  const [testMode, setTestMode] = useState<'single' | 'batch' | 'ai-detect' | 'composite-check' | 'trust-score' | 'text-analysis' | 'face-verify' | 'moderation'>('single');
  
  // Moderation testing state
  const [moderationProfiles, setModerationProfiles] = useState<ModerationTestProfile[]>([]);
  const [moderationImages, setModerationImages] = useState<string[]>([]);
  const [moderationTextData, setModerationTextData] = useState({
    user_id: '',
    bio: '',
    prompts: [{ question: '', answer: '' }],
    metadata: ''
  });
  const [moderationGlobalConfig, setModerationGlobalConfig] = useState({
    face_detect: true,
    ai_image_detect: true
  });
  const [isLoadingModeration, setIsLoadingModeration] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<string>('');
  const [compositeOptions, setCompositeOptions] = useState({
    face_detect: false,
    ai_image_detect: false
  });
  const [trustScoreConfigurations, setTrustScoreConfigurations] = useState<Array<{
    face_detect: boolean;
    ai_image_detect: boolean;
  }>>([]);
  const [trustScoreImageCount, setTrustScoreImageCount] = useState<number>(0);
  const [trustScoreTextData, setTrustScoreTextData] = useState({
    bio: '',
    prompts: [{ question: '', answer: '' }],
    metadata: '',
    image_block: false
  });
  const [textAnalysisData, setTextAnalysisData] = useState({
    bio: '',
    prompts: [{ question: '', answer: '' }],
    metadata: ''
  });
  const [dragActive, setDragActive] = useState(false);
  const [similarityThreshold, setSimilarityThreshold] = useState<number>(90);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const batchFileInputRef = useRef<HTMLInputElement>(null);
  const selfieFileInputRef = useRef<HTMLInputElement>(null);
  const profileFileInputRef = useRef<HTMLInputElement>(null);
  const moderationFileInputRef = useRef<HTMLInputElement>(null);
  
  // Set default API key when available
  useEffect(() => {
    if (apiKeys.length > 0 && !selectedApiKey) {
      setSelectedApiKey(apiKeys[0].id);
    }
  }, [apiKeys, selectedApiKey]);

  const currentApiUrl = API_CONFIG.AWS_BASE_URL;

  // Test mode configurations
  const testModes = [
    {
      id: 'moderation',
      name: 'Moderation Submit',
      description: 'Submit profile for moderation',
      endpoint: '/api/moderation/submit-profile',
      icon: ShieldCheck,
      color: '#8b5cf6'
    },
    {
      id: 'single',
      name: 'Face Detection',
      description: 'Detect faces in images with detailed analysis',
      endpoint: '/api/test/face-detect',
      icon: Target,
      color: '#8b5cf6'
    },
    {
      id: 'batch',
      name: 'Batch Face Detection',
      description: 'Detect faces in multiple images at once',
      endpoint: '/api/test/face-detect-batch',
      icon: Layers,
      color: '#06b6d4'
    },
    {
      id: 'ai-detect',
      name: 'AI Image Detection',
      description: 'Detect AI-generated images',
      endpoint: '/api/test/ai-image-detect',
      icon: Brain,
      color: '#10b981'
    },
    {
      id: 'composite-check',
      name: 'Composite Check',
      description: 'Combine a combination of checks into one',
      endpoint: '/api/test/composite-check',
      icon: Activity,
      color: '#f59e0b'
    },
    {
      id: 'text-analysis',
      name: 'Text Analysis',
      description: 'Analyze bio, prompts, and metadata for authenticity',
      endpoint: '/api/test/text-analysis',
      icon: Brain,
      color: '#6366f1'
    },
    {
      id: 'trust-score',
      name: 'Trust Score',
      description: 'Analyze everything in one request',
      endpoint: '/api/test/trust-score',
      icon: Zap,
      color: '#ef4444'
    },
    {
      id: 'face-verify',
      name: 'Face Verify',
      description: 'Verify if selfie matches any profile images',
      endpoint: '/api/test/face-verify',
      icon: Eye,
      color: '#ec4899'
    }
  ];

  // --- Image Upload & Compression ---
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      const maxSize = 800;
      let { width, height } = img;

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);

      const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.8);
      setSelectedImage(compressedDataUrl);
    };

    img.src = URL.createObjectURL(file);
  };

  // --- Batch Image Upload & Compression ---
  const handleBatchImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    if (files.length > 10) {
      alert("Maximum 10 images allowed for batch processing");
      return;
    }

    const processFile = (file: File): Promise<string> => {
      return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();

        img.onload = () => {
          const maxSize = 800;
          let { width, height } = img;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.8);
          resolve(compressedDataUrl);
        };

        img.src = URL.createObjectURL(file);
      });
    };

    Promise.all(files.map(processFile)).then((compressedImages) => {
      setSelectedImages(compressedImages);
    });
  };

  const removeBatchImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  // --- Face Verify Image Upload Handlers ---
  const handleSelfieUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      const maxSize = 800;
      let { width, height } = img;

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);

      const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.8);
      setSelfieImage(compressedDataUrl);
    };

    img.src = URL.createObjectURL(file);
  };

  const handleProfileImagesUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    if (files.length + profileImages.length > 10) {
      alert("Maximum 10 profile images allowed");
      return;
    }

    const processFile = (file: File): Promise<string> => {
      return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();

        img.onload = () => {
          const maxSize = 800;
          let { width, height } = img;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.8);
          resolve(compressedDataUrl);
        };

        img.src = URL.createObjectURL(file);
      });
    };

    Promise.all(files.map(processFile)).then((compressedImages) => {
      setProfileImages(prev => [...prev, ...compressedImages]);
    });
  };

  const removeProfileImage = (index: number) => {
    setProfileImages(prev => prev.filter((_, i) => i !== index));
  };

  // --- Moderation Image Upload Handler ---
  const handleModerationImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    if (files.length + moderationImages.length > 10) {
      alert("Maximum 10 images allowed for moderation");
      return;
    }

    const processFile = (file: File): Promise<string> => {
      return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();

        img.onload = () => {
          const maxSize = 800;
          let { width, height } = img;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.8);
          resolve(compressedDataUrl);
        };

        img.src = URL.createObjectURL(file);
      });
    };

    Promise.all(files.map(processFile)).then((compressedImages) => {
      setModerationImages(prev => [...prev, ...compressedImages]);
    });
  };

  const removeModerationImage = (index: number) => {
    setModerationImages(prev => prev.filter((_, i) => i !== index));
  };

  // --- Drag and Drop Handlers ---
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragOut = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      if (testMode === 'single' || testMode === 'ai-detect') {
        const file = e.dataTransfer.files[0];
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();

        img.onload = () => {
          const maxSize = 800;
          let { width, height } = img;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.8);
          setSelectedImage(compressedDataUrl);
        };

        img.src = URL.createObjectURL(file);
      } else {
        handleBatchImageUpload({ target: { files: e.dataTransfer.files } } as any);
      }
    }
  };

  // --- Test Face Detection ---
  const testFaceDetection = async () => {
    if (!selectedImage) {
      alert("Please select an image first");
      return;
    }
  
    setIsLoading(true);
    setTestResult(null);
  
    try {
      const apiUrl = `${currentApiUrl}/api/test/face-detect`;
      const headers: any = {
        "Content-Type": "application/json",
      };

      const base64Image = selectedImage.includes(',')
        ? selectedImage.split(',')[1]
        : selectedImage;
    
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ image: base64Image }),
      });
    
      const result = await response.json();
    
      setTestResult({
        success: result.success,
        data: result,
        error: result.success ? undefined : result.error,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Test AI Detection ---
  const testAIDetection = async () => {
    if (!selectedImage) {
      alert("Please select an image first");
      return;
    }

    setIsLoading(true);
    setTestResult(null);
  
    try {
      const apiUrl = `${currentApiUrl}/api/test/ai-image-detect`;
      const headers: any = {
        "Content-Type": "application/json",
      };

      const base64Image = selectedImage.includes(',')
        ? selectedImage.split(',')[1]
        : selectedImage;
    
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ image: base64Image }),
      });
    
      const result = await response.json();
    
      setTestResult({
        success: result.success,
        data: result,
        error: result.success ? undefined : result.error,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Test Batch Processing ---
  const testBatchProcessing = async () => {
    if (selectedImages.length === 0) {
      alert("Please select images first");
      return;
    }

    setIsLoading(true);
    setBatchTestResult(null);
  
    try {
      const apiUrl = `${currentApiUrl}/api/test/face-detect-batch`;
      const headers: any = {
        "Content-Type": "application/json",
      };

      const base64Images = selectedImages.map(img => 
        img.includes(',') ? img.split(',')[1] : img
      );
    
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ images: base64Images }),
      });
    
      const result = await response.json();
    
      setBatchTestResult({
        success: result.success,
        data: result,
        error: result.success ? undefined : result.error,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setBatchTestResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Test Composite Check ---
  const testCompositeCheck = async () => {
    if (!selectedImage) {
      alert("Please select an image first");
      return;
    }

    if (!compositeOptions.face_detect && !compositeOptions.ai_image_detect) {
      alert("Please select at least one detection option");
      return;
    }

    setIsLoading(true);
    setTestResult(null);
  
    try {
      const apiUrl = `${currentApiUrl}/api/test/composite-check`;
      const headers: any = {
        "Content-Type": "application/json",
      };

      const base64Image = selectedImage.includes(',')
        ? selectedImage.split(',')[1]
        : selectedImage;
    
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ 
          image: base64Image,
          face_detect: compositeOptions.face_detect,
          ai_image_detect: compositeOptions.ai_image_detect
        }),
      });
    
      const result = await response.json();
    
      setTestResult({
        success: result.success,
        data: result,
        error: result.success ? undefined : result.error,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Test Text Analysis ---
  const testTextAnalysis = async () => {
    const { bio, prompts, metadata } = textAnalysisData;
    
    if (!bio && prompts.every(p => !p.question && !p.answer) && !metadata) {
      alert("Please provide at least one of: bio, prompts, or metadata");
      return;
    }

    setIsLoading(true);
    setTestResult(null);

    try {
      const apiUrl = `${currentApiUrl}/api/test/text-analysis`;
      const headers: any = {
        "Content-Type": "application/json",
      };

      const body: any = {};
      if (bio) body.bio = bio;
      if (prompts.some(p => p.question || p.answer)) {
        body.prompts = prompts.filter(p => p.question || p.answer);
      }
      if (metadata) body.metadata = metadata;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      const result = await response.json();

      setTestResult({
        success: result.success,
        data: result,
        error: result.success ? undefined : result.error,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Test Trust Score ---
  const testTrustScore = async () => {
    if (selectedImages.length === 0) {
      alert("Please select at least one image");
      return;
    }

    // Note: configurations are optional (defaults to all true), but we validate images are provided

    setIsLoading(true);
    setBatchTestResult(null);

    try {
      const apiUrl = `${currentApiUrl}/api/test/trust-score`;
      const headers: any = {
        "Content-Type": "application/json",
      };

      const base64Images = selectedImages.map(img => 
        img.includes(',') ? img.split(',')[1] : img
      );

      // Build request body
      const body: any = {
        images: base64Images
      };

      // Add global configuration (only if user has customized it)
      // Server defaults to all true if not provided
      if (trustScoreConfigurations.length > 0) {
        const firstConfig = trustScoreConfigurations[0];
        // Only send global_configuration if it's different from defaults (all true)
        if (firstConfig.face_detect === false || firstConfig.ai_image_detect === false) {
          body.global_configuration = {
            face_detect: firstConfig.face_detect !== undefined ? firstConfig.face_detect : true,
            ai_image_detect: firstConfig.ai_image_detect !== undefined ? firstConfig.ai_image_detect : true
          };
        }

        // Add per-image configuration for any image with non-default config
        const perImageConfig: any = {};
        trustScoreConfigurations.forEach((config, index) => {
          // Only add per-image config if:
          // 1. It's not the first config (which applies globally), OR
          // 2. It has any false values (which override defaults)
          if (index > 0 || (config.face_detect === false || config.ai_image_detect === false)) {
            perImageConfig[index.toString()] = {
              face_detect: config.face_detect !== undefined ? config.face_detect : true,
              ai_image_detect: config.ai_image_detect !== undefined ? config.ai_image_detect : true
            };
          }
        });
        if (Object.keys(perImageConfig).length > 0) {
          body.per_image_configuration = perImageConfig;
        }
      }

      // Add text fields if provided
      const { bio, prompts, metadata, image_block } = trustScoreTextData;
      if (bio && bio.trim().length > 0) {
        body.bio = bio.trim();
      }
      if (prompts.some(p => p.question || p.answer)) {
        body.prompts = prompts.filter(p => p.question || p.answer);
      }
      if (metadata && metadata.trim().length > 0) {
        body.metadata = metadata.trim();
      }
      if (image_block) {
        body.image_block = true;
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      const result = await response.json();

      setBatchTestResult({
        success: result.success,
        data: result,
        error: result.success ? undefined : result.error,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setBatchTestResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Test Face Verify ---
  const testFaceVerify = async () => {
    if (!selfieImage) {
      alert("Please select a selfie image first");
      return;
    }

    if (profileImages.length === 0) {
      alert("Please select at least one profile image");
      return;
    }

    setIsLoading(true);
    setTestResult(null);
    setBatchTestResult(null);

    try {
      const apiUrl = `${currentApiUrl}/api/test/face-verify`;
      const headers: any = {
        "Content-Type": "application/json",
      };

      const base64Selfie = selfieImage.includes(',')
        ? selfieImage.split(',')[1]
        : selfieImage;

      const base64ProfileImages = profileImages.map(img => 
        img.includes(',') ? img.split(',')[1] : img
      );

      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({
          selfie: base64Selfie,
          profile_images: base64ProfileImages,
          similarity_threshold: similarityThreshold
        }),
      });

      const result = await response.json();

      setTestResult({
        success: result.success,
        data: result,
        error: result.success ? undefined : result.error,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Test Moderation Submit ---
  const testModerationSubmit = async () => {
    if (moderationImages.length === 0) {
      alert("Please upload at least one image");
      return;
    }

    if (!selectedApiKey) {
      alert("Please select an API key");
      return;
    }

    const apiKey = apiKeys.find(k => k.id === selectedApiKey);
    if (!apiKey) {
      alert("Selected API key not found");
      return;
    }

    setIsLoadingModeration(true);

    try {
      const base64Images = moderationImages.map(img => 
        img.includes(',') ? img.split(',')[1] : img
      );

      const profileData: any = {
        images: base64Images
      };

      if (moderationTextData.user_id) {
        profileData.user_id = moderationTextData.user_id;
      }
      if (moderationTextData.bio && moderationTextData.bio.trim()) {
        profileData.bio = moderationTextData.bio.trim();
      }
      if (moderationTextData.prompts.some(p => p.question || p.answer)) {
        profileData.prompts = moderationTextData.prompts.filter(p => p.question || p.answer);
      }
      if (moderationTextData.metadata && moderationTextData.metadata.trim()) {
        try {
          profileData.metadata = JSON.parse(moderationTextData.metadata);
        } catch {
          profileData.metadata = moderationTextData.metadata;
        }
      }

      if (moderationGlobalConfig.face_detect === false || moderationGlobalConfig.ai_image_detect === false) {
        profileData.global_configuration = moderationGlobalConfig;
      }

      const result = await submitProfileForModeration(apiKey.key, profileData);

      if (result.success) {
        // Create webhook payload simulation
        const webhookPayload = {
          event_id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          profile_id: result.profile_id,
          status: result.status,
          trust_score: result.trust_score,
          timestamp: new Date().toISOString(),
          user_id: moderationTextData.user_id || undefined,
          message: result.message
        };

        // Create test profile for bins
        const testProfile: ModerationTestProfile = {
          id: `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          profile_id: result.profile_id,
          user_id: moderationTextData.user_id,
          status: result.status as 'approved' | 'rejected' | 'pending',
          trust_score: result.trust_score || 0,
          images: moderationImages,
          bio: moderationTextData.bio || undefined,
          prompts: moderationTextData.prompts.filter(p => p.question || p.answer).length > 0 
            ? moderationTextData.prompts.filter(p => p.question || p.answer) 
            : undefined,
          metadata: moderationTextData.metadata ? (() => {
            try {
              return JSON.parse(moderationTextData.metadata);
            } catch {
              return moderationTextData.metadata;
            }
          })() : undefined,
          timestamp: new Date().toISOString(),
          webhook_payload: webhookPayload
        };

        setModerationProfiles(prev => [...prev, testProfile]);
      }
    } catch (error) {
      console.error('Moderation test error:', error);
      alert(error instanceof Error ? error.message : 'Failed to submit profile for moderation');
    } finally {
      setIsLoadingModeration(false);
    }
  };

  // --- Run Test ---
  const runTest = () => {
    switch (testMode) {
      case 'single':
        testFaceDetection();
        break;
      case 'ai-detect':
        testAIDetection();
        break;
      case 'batch':
        testBatchProcessing();
        break;
      case 'composite-check':
        testCompositeCheck();
        break;
      case 'trust-score':
        testTrustScore();
        break;
      case 'text-analysis':
        testTextAnalysis();
        break;
      case 'face-verify':
        testFaceVerify();
        break;
      case 'moderation':
        testModerationSubmit();
        break;
    }
  };

  // --- Clear Results ---
  const clearResults = () => {
    setTestResult(null);
    setBatchTestResult(null);
  };

  // --- Clear Images ---
  const clearImages = () => {
    setSelectedImage(null);
    setSelectedImages([]);
    setSelfieImage(null);
    setProfileImages([]);
    setTestResult(null);
    setBatchTestResult(null);
    if (testMode !== 'moderation') {
      setModerationImages([]);
      setModerationTextData({
        user_id: '',
        bio: '',
        prompts: [{ question: '', answer: '' }],
        metadata: ''
      });
    }
  };

  // --- Clear Moderation Bins ---
  const clearModerationBins = () => {
    setModerationProfiles([]);
  };

  const currentMode = testModes.find(mode => mode.id === testMode);

  // Moderation Profile Card Wrapper (to manage state)
  const ModerationProfileCardWrapper: React.FC<{ profile: ModerationTestProfile }> = ({ profile }) => {
    const [expanded, setExpanded] = useState(false);
    const [showWebhook, setShowWebhook] = useState(false);
    return (
      <ModerationProfileCard 
        profile={profile} 
        expanded={expanded} 
        setExpanded={setExpanded} 
        showWebhook={showWebhook} 
        setShowWebhook={setShowWebhook} 
      />
    );
  };

  // Moderation Profile Card Component
  const ModerationProfileCard: React.FC<{ 
    profile: ModerationTestProfile;
    expanded: boolean;
    setExpanded: (expanded: boolean) => void;
    showWebhook: boolean;
    setShowWebhook: (show: boolean) => void;
  }> = ({ profile, expanded, setExpanded, showWebhook, setShowWebhook }) => {
    return (
      <div className="test-profile-card">
        <div className="test-profile-header">
          <div className="test-profile-main">
            <div className="test-profile-id">
              {profile.user_id ? `User: ${profile.user_id}` : `Profile: ${profile.profile_id?.substring(0, 8) || profile.id.substring(0, 8)}`}
            </div>
            <div className={`test-profile-status-badge status-${profile.status}`}>
              {profile.status === 'approved' && <CheckCircle2 size={14} />}
              {profile.status === 'rejected' && <XCircle size={14} />}
              {profile.status === 'pending' && <Clock size={14} />}
              <span>{profile.status.charAt(0).toUpperCase() + profile.status.slice(1)}</span>
            </div>
          </div>
          <div className="test-profile-meta">
            <span className="test-meta-label">Trust Score:</span>
            <span className={`test-meta-value score-${profile.trust_score >= 70 ? 'high' : profile.trust_score >= 30 ? 'medium' : 'low'}`}>
              {profile.trust_score}
            </span>
          </div>
          <div className="test-profile-actions">
            <button
              className="test-action-button"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Collapse' : 'Expand'}
            </button>
            <button
              className="test-action-button webhook-button"
              onClick={() => setShowWebhook(!showWebhook)}
            >
              <WebhookIcon size={14} />
              Webhook
            </button>
          </div>
        </div>

        {expanded && (
          <div className="test-profile-details">
            {profile.images && profile.images.length > 0 && (
              <div className="test-detail-section">
                <h5>Images ({profile.images.length})</h5>
                <div className="test-images-grid">
                  {profile.images.slice(0, 6).map((img, idx) => (
                    <img key={idx} src={img} alt={`Image ${idx + 1}`} className="test-image-preview" />
                  ))}
                </div>
              </div>
            )}
            {profile.bio && (
              <div className="test-detail-section">
                <h5>Bio</h5>
                <p>{profile.bio}</p>
              </div>
            )}
            {profile.prompts && profile.prompts.length > 0 && (
              <div className="test-detail-section">
                <h5>Prompts</h5>
                {profile.prompts.map((p, idx) => (
                  <div key={idx} className="test-prompt-item">
                    <strong>Q:</strong> {p.question || 'N/A'} | <strong>A:</strong> {p.answer || 'N/A'}
                  </div>
                ))}
              </div>
            )}
            {profile.metadata && (
              <div className="test-detail-section">
                <h5>Metadata</h5>
                <pre className="test-metadata">{JSON.stringify(profile.metadata, null, 2)}</pre>
              </div>
            )}
          </div>
        )}

        {showWebhook && profile.webhook_payload && (
          <div className="webhook-simulation">
            <h5>Webhook Payload Simulation</h5>
            <pre className="webhook-json">{JSON.stringify(profile.webhook_payload, null, 2)}</pre>
            <p className="webhook-note">
              This is a simulation of what would be sent to your webhook URL when a profile is submitted.
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="testing-page">
      <PageHeader
        icon={Activity}
        title="Testing"
        subtitle="Test and validate your API endpoints with real data"
        actions={
          <button 
            className="refresh-button"
            onClick={clearResults}
            disabled={!testResult && !batchTestResult}
          >
            <RefreshCw className="refresh-icon" />
            Clear Results
          </button>
        }
      />

      {/* Test Mode Selector */}
      <div className="test-mode-section">
        <div className="api-options">
          {testModes.map((mode) => {
            const IconComponent = mode.icon;
            return (
              <button
                key={mode.id}
                className={`api-option ${testMode === mode.id ? 'active' : ''}`}
                onClick={() => setTestMode(mode.id as any)}
              >
                <div className="option-icon">
                  <IconComponent className="icon" />
                </div>
                <div className="option-content">
                  <div className="option-name">{mode.name}</div>
                  <div className="option-description">{mode.description}</div>
                  <div className="option-endpoint">{mode.endpoint}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Image Upload Section or Text Input Section */}
      {testMode === 'face-verify' ? (
        <div className="face-verify-section">
          <h3 className="section-title">Face Verification</h3>
          
          {/* Selfie Upload */}
          <div className="face-verify-upload-group">
            <label className="input-label">Selfie Image (Required)</label>
            <div 
              className={`image-upload-area ${dragActive ? 'drag-active' : ''} ${selfieImage ? 'has-image' : ''}`}
              onDragEnter={handleDragIn}
              onDragLeave={handleDragOut}
              onDragOver={handleDrag}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                  const file = e.dataTransfer.files[0];
                  const canvas = document.createElement("canvas");
                  const ctx = canvas.getContext("2d");
                  const img = new Image();
                  img.onload = () => {
                    const maxSize = 800;
                    let { width, height } = img;
                    if (width > height) {
                      if (width > maxSize) {
                        height = (height * maxSize) / width;
                        width = maxSize;
                      }
                    } else {
                      if (height > maxSize) {
                        width = (width * maxSize) / height;
                        height = maxSize;
                      }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    ctx?.drawImage(img, 0, 0, width, height);
                    const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.8);
                    setSelfieImage(compressedDataUrl);
                  };
                  img.src = URL.createObjectURL(file);
                }
              }}
            >
              {selfieImage ? (
                <div className="image-preview">
                  <img src={selfieImage} alt="Selfie" className="preview-image" />
                  <button 
                    className="remove-image-btn"
                    onClick={() => setSelfieImage(null)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <Upload className="upload-icon" />
                  <p className="upload-text">Drag & drop selfie image here or click to browse</p>
                </div>
              )}
              <input
                ref={selfieFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleSelfieUpload}
                className="file-input"
              />
            </div>
          </div>

          {/* Profile Images Upload */}
          <div className="face-verify-upload-group">
            <label className="input-label">Profile Images (Required, up to 10)</label>
            <div 
              className={`batch-upload-area ${dragActive ? 'drag-active' : ''} ${profileImages.length > 0 ? 'has-images' : ''}`}
              onDragEnter={handleDragIn}
              onDragLeave={handleDragOut}
              onDragOver={handleDrag}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
                if (e.dataTransfer.files) {
                  handleProfileImagesUpload({ target: { files: e.dataTransfer.files } } as any);
                }
              }}
            >
              {profileImages.length > 0 ? (
                <div className="batch-images-grid">
                  {profileImages.map((image, index) => (
                    <div key={index} className="batch-image-item">
                      <img src={image} alt={`Profile ${index + 1}`} className="batch-preview" />
                      <button 
                        className="remove-batch-btn"
                        onClick={() => removeProfileImage(index)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {profileImages.length < 10 && (
                    <div className="add-more-btn" onClick={() => profileFileInputRef.current?.click()}>
                      <Upload size={24} />
                      <span>Add More</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="batch-upload-placeholder">
                  <Upload className="upload-icon" />
                  <p className="upload-text">Drag & drop profile images here or click to browse</p>
                  <p className="upload-hint">Up to 10 images</p>
                </div>
              )}
              <input
                ref={profileFileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleProfileImagesUpload}
                className="file-input"
              />
            </div>
          </div>

          {/* Similarity Threshold */}
          <div className="face-verify-upload-group">
            <label className="input-label">Similarity Threshold (0-100)</label>
            <input
              type="number"
              min="0"
              max="100"
              value={similarityThreshold}
              onChange={(e) => setSimilarityThreshold(Math.max(0, Math.min(100, parseInt(e.target.value) || 90)))}
              className="text-input"
              style={{ maxWidth: '200px' }}
            />
            <p className="input-hint">Default: 90. Higher values require more similarity to match.</p>
          </div>

          {(selfieImage || profileImages.length > 0) && (
            <div className="batch-actions">
              <button className="action-btn" onClick={clearImages}>
                <Trash2 size={16} />
                Clear All Images
              </button>
              <span className="image-count">
                {selfieImage ? '1 selfie' : '0 selfies'}, {profileImages.length} profile image{profileImages.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      ) : testMode === 'text-analysis' ? (
        <div className="text-input-section">
          <h3 className="section-title">Text Analysis Input</h3>
          
          <div className="text-input-group">
            <label className="input-label">Bio (Optional)</label>
            <textarea
              className="text-input"
              placeholder="Enter user bio..."
              value={textAnalysisData.bio}
              onChange={(e) => setTextAnalysisData(prev => ({ ...prev, bio: e.target.value }))}
              rows={3}
            />
          </div>

          <div className="text-input-group">
            <label className="input-label">Prompts (Optional)</label>
            {textAnalysisData.prompts.map((prompt, index) => (
              <div key={index} className="prompt-item">
                <input
                  type="text"
                  className="text-input"
                  placeholder="Question"
                  value={prompt.question}
                  onChange={(e) => setTextAnalysisData(prev => ({
                    ...prev,
                    prompts: prev.prompts.map((p, i) => 
                      i === index ? { ...p, question: e.target.value } : p
                    )
                  }))}
                />
                <input
                  type="text"
                  className="text-input"
                  placeholder="Answer"
                  value={prompt.answer}
                  onChange={(e) => setTextAnalysisData(prev => ({
                    ...prev,
                    prompts: prev.prompts.map((p, i) => 
                      i === index ? { ...p, answer: e.target.value } : p
                    )
                  }))}
                />
                {textAnalysisData.prompts.length > 1 && (
                  <button
                    className="remove-prompt-btn"
                    onClick={() => setTextAnalysisData(prev => ({
                      ...prev,
                      prompts: prev.prompts.filter((_, i) => i !== index)
                    }))}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            <button
              className="add-prompt-btn"
              onClick={() => setTextAnalysisData(prev => ({
                ...prev,
                prompts: [...prev.prompts, { question: '', answer: '' }]
              }))}
            >
              <Plus size={16} />
              Add Prompt
            </button>
          </div>

          <div className="text-input-group">
            <label className="input-label">Metadata (Optional)</label>
            <textarea
              className="text-input"
              placeholder="Enter metadata (e.g., IP=192.168.0.3, device=Android, region=CA)..."
              value={textAnalysisData.metadata}
              onChange={(e) => setTextAnalysisData(prev => ({ ...prev, metadata: e.target.value }))}
              rows={4}
            />
          </div>
        </div>
      ) : testMode === 'moderation' ? (
        <div className="moderation-testing-section">
          <h3 className="section-title">Moderation Profile Submission</h3>
          
          {/* API Key Selector */}
          <div className="moderation-api-key-selector">
            <label className="input-label">API Key</label>
            <select
              className="text-input"
              value={selectedApiKey}
              onChange={(e) => setSelectedApiKey(e.target.value)}
            >
              <option value="">Select API Key</option>
              {apiKeys.map(key => (
                <option key={key.id} value={key.id}>
                  {key.title || key.key_prefix}
                </option>
              ))}
            </select>
          </div>

          {/* Image Upload */}
          <div className="text-input-group">
            <label className="input-label">Images (Required, up to 10)</label>
            <div 
              className={`batch-upload-area ${dragActive ? 'drag-active' : ''} ${moderationImages.length > 0 ? 'has-images' : ''}`}
              onDragEnter={handleDragIn}
              onDragLeave={handleDragOut}
              onDragOver={handleDrag}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
                if (e.dataTransfer.files) {
                  handleModerationImageUpload({ target: { files: e.dataTransfer.files } } as any);
                }
              }}
            >
              {moderationImages.length > 0 ? (
                <div className="batch-images-grid">
                  {moderationImages.map((image, index) => (
                    <div key={index} className="batch-image-item">
                      <img src={image} alt={`Moderation ${index + 1}`} className="batch-preview" />
                      <button 
                        className="remove-batch-btn"
                        onClick={() => removeModerationImage(index)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {moderationImages.length < 10 && (
                    <div className="add-more-btn" onClick={() => moderationFileInputRef.current?.click()}>
                      <Upload size={24} />
                      <span>Add More</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="batch-upload-placeholder">
                  <Upload className="upload-icon" />
                  <p className="upload-text">Drag & drop images here or click to browse</p>
                  <p className="upload-hint">Up to 10 images</p>
                </div>
              )}
              <input
                ref={moderationFileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleModerationImageUpload}
                className="file-input"
              />
            </div>
          </div>

          {/* Global Configuration */}
          <div className="text-input-group">
            <label className="input-label">Image Processing Configuration</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={moderationGlobalConfig.face_detect}
                  onChange={(e) => setModerationGlobalConfig(prev => ({ ...prev, face_detect: e.target.checked }))}
                />
                <span className="checkmark"></span>
                <span>Face Detection</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={moderationGlobalConfig.ai_image_detect}
                  onChange={(e) => setModerationGlobalConfig(prev => ({ ...prev, ai_image_detect: e.target.checked }))}
                />
                <span className="checkmark"></span>
                <span>AI Image Detection</span>
              </label>
            </div>
          </div>

          {/* User ID */}
          <div className="text-input-group">
            <label className="input-label">User ID (Optional)</label>
            <input
              type="text"
              className="text-input"
              placeholder="Enter user ID..."
              value={moderationTextData.user_id}
              onChange={(e) => setModerationTextData(prev => ({ ...prev, user_id: e.target.value }))}
            />
          </div>

          {/* Bio */}
          <div className="text-input-group">
            <label className="input-label">Bio (Optional)</label>
            <textarea
              className="text-input"
              placeholder="Enter user bio..."
              value={moderationTextData.bio}
              onChange={(e) => setModerationTextData(prev => ({ ...prev, bio: e.target.value }))}
              rows={3}
            />
          </div>

          {/* Prompts */}
          <div className="text-input-group">
            <label className="input-label">Prompts & Answers (Optional)</label>
            {moderationTextData.prompts.map((prompt, index) => (
              <div key={index} className="prompt-item">
                <input
                  type="text"
                  className="text-input"
                  placeholder="Question"
                  value={prompt.question}
                  onChange={(e) => setModerationTextData(prev => ({
                    ...prev,
                    prompts: prev.prompts.map((p, i) => 
                      i === index ? { ...p, question: e.target.value } : p
                    )
                  }))}
                />
                <input
                  type="text"
                  className="text-input"
                  placeholder="Answer"
                  value={prompt.answer}
                  onChange={(e) => setModerationTextData(prev => ({
                    ...prev,
                    prompts: prev.prompts.map((p, i) => 
                      i === index ? { ...p, answer: e.target.value } : p
                    )
                  }))}
                />
                {moderationTextData.prompts.length > 1 && (
                  <button
                    className="remove-prompt-btn"
                    onClick={() => setModerationTextData(prev => ({
                      ...prev,
                      prompts: prev.prompts.filter((_, i) => i !== index)
                    }))}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            <button
              className="add-prompt-btn"
              onClick={() => setModerationTextData(prev => ({
                ...prev,
                prompts: [...prev.prompts, { question: '', answer: '' }]
              }))}
            >
              <Plus size={16} />
              Add Prompt
            </button>
          </div>

          {/* Metadata */}
          <div className="text-input-group">
            <label className="input-label">Metadata (Optional)</label>
            <textarea
              className="text-input"
              placeholder="Enter metadata (JSON or text)..."
              value={moderationTextData.metadata}
              onChange={(e) => setModerationTextData(prev => ({ ...prev, metadata: e.target.value }))}
              rows={4}
            />
          </div>

          {moderationImages.length > 0 && (
            <div className="batch-actions">
              <button className="action-btn" onClick={() => {
                setModerationImages([]);
                setModerationTextData({
                  user_id: '',
                  bio: '',
                  prompts: [{ question: '', answer: '' }],
                  metadata: ''
                });
              }}>
                <Trash2 size={16} />
                Clear All
              </button>
              <span className="image-count">{moderationImages.length} image{moderationImages.length !== 1 ? 's' : ''} selected</span>
            </div>
          )}
        </div>
      ) : (
        <div className="image-upload-section">
          <h3 className="section-title">Upload Images</h3>
          
          {testMode === 'single' || testMode === 'ai-detect' || testMode === 'composite-check' ? (
          <div 
            className={`image-upload-area ${dragActive ? 'drag-active' : ''} ${selectedImage ? 'has-image' : ''}`}
            onDragEnter={handleDragIn}
            onDragLeave={handleDragOut}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
                {selectedImage ? (
                  <div className="image-preview">
                <img src={selectedImage} alt="Selected" className="preview-image" />
                <button 
                  className="remove-image-btn"
                  onClick={() => setSelectedImage(null)}
                >
                  <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
              <div className="upload-placeholder">
                <Upload className="upload-icon" />
                <p className="upload-text">Drag & drop an image here or click to browse</p>
                <p className="upload-hint">Supports JPG, PNG, GIF (max 10MB)</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
              className="file-input"
                />
              </div>
        ) : (
          <div 
            className={`batch-upload-area ${dragActive ? 'drag-active' : ''} ${selectedImages.length > 0 ? 'has-images' : ''}`}
            onDragEnter={handleDragIn}
            onDragLeave={handleDragOut}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedImages.length > 0 ? (
              <div className="batch-images-grid">
                {selectedImages.map((image, index) => (
                  <div key={index} className="batch-image-item">
                    <img src={image} alt={`Batch ${index + 1}`} className="batch-preview" />
                    <button 
                      className="remove-batch-btn"
                      onClick={() => removeBatchImage(index)}
                    >
                      <Trash2 size={14} />
                    </button>
            </div>
                ))}
                {selectedImages.length < 10 && (
                  <div className="add-more-btn" onClick={() => batchFileInputRef.current?.click()}>
                    <Upload size={24} />
                    <span>Add More</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="batch-upload-placeholder">
                <Upload className="upload-icon" />
                <p className="upload-text">Drag & drop multiple images here or click to browse</p>
                <p className="upload-hint">Up to 10 images, supports JPG, PNG, GIF</p>
              </div>
            )}
            <input
              ref={batchFileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleBatchImageUpload}
              className="file-input"
            />
          </div>
        )}

        {selectedImage && (
          <div className="image-actions">
            <button className="action-btn" onClick={clearImages}>
              <Trash2 size={16} />
              Clear Image
            </button>
                </div>
        )}

        {selectedImages.length > 0 && (
          <div className="batch-actions">
            <button className="action-btn" onClick={clearImages}>
              <Trash2 size={16} />
              Clear All Images
            </button>
            <span className="image-count">{selectedImages.length} image{selectedImages.length !== 1 ? 's' : ''} selected</span>
          </div>
        )}
      </div>
      )}

      {/* Configuration Section */}
      {(testMode === 'composite-check' || testMode === 'trust-score') && (
        <div className="configuration-section">
          <h3 className="section-title">Configuration</h3>
          
          {testMode === 'composite-check' && (
            <div className="composite-options">
                  <div className="option-group">
                <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={compositeOptions.face_detect}
                    onChange={(e) => setCompositeOptions(prev => ({ ...prev, face_detect: e.target.checked }))}
                  />
                  <span className="checkmark"></span>
                  <div className="option-content">
                    <span className="option-name">Face Detection</span>
                    <span className="option-desc">Detect faces in the image</span>
                  </div>
                    </label>
                  </div>
                  <div className="option-group">
                <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={compositeOptions.ai_image_detect}
                    onChange={(e) => setCompositeOptions(prev => ({ ...prev, ai_image_detect: e.target.checked }))}
                  />
                  <span className="checkmark"></span>
                  <div className="option-content">
                    <span className="option-name">AI Image Detection</span>
                    <span className="option-desc">Detect AI-generated content</span>
                  </div>
                    </label>
                </div>
              </div>
            )}

          {testMode === 'trust-score' && (
            <div className="trust-score-config">
              <div className="config-header">
                <h4>Image Processing Rules</h4>
                <button
                  className="add-config-btn"
                  onClick={() => setTrustScoreConfigurations(prev => [...prev, { face_detect: false, ai_image_detect: false }])}
                >
                  <Plus size={16} />
                  Add Rule
                </button>
                      </div>
              <div className="config-list">
                {trustScoreConfigurations.map((config, index) => (
                  <div key={index} className="config-item">
                    <div className="config-header">
                      <span className="config-label">Rule {index + 1}</span>
                      <button
                        className="remove-config-btn"
                        onClick={() => setTrustScoreConfigurations(prev => prev.filter((_, i) => i !== index))}
                      >
                        <Trash2 size={14} />
                      </button>
                          </div>
                          <div className="config-options">
                      <label className="checkbox-label">
                              <input
                                type="checkbox"
                          checked={config.face_detect}
                          onChange={(e) => setTrustScoreConfigurations(prev => prev.map((c, i) => 
                            i === index ? { ...c, face_detect: e.target.checked } : c
                          ))}
                        />
                        <span className="checkmark"></span>
                              <span>Face Detection</span>
                            </label>
                      <label className="checkbox-label">
                              <input
                                type="checkbox"
                          checked={config.ai_image_detect}
                          onChange={(e) => setTrustScoreConfigurations(prev => prev.map((c, i) => 
                            i === index ? { ...c, ai_image_detect: e.target.checked } : c
                          ))}
                        />
                        <span className="checkmark"></span>
                              <span>AI Detection</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                        </div>
                      )}

      {/* Text Input Section for Trust Score */}
      {testMode === 'trust-score' && (
        <div className="text-input-section">
          <h3 className="section-title">Text Analysis (Optional)</h3>
          
          <div className="text-input-group">
            <label className="input-label">Bio (Optional)</label>
            <textarea
              className="text-input"
              placeholder="Enter user bio..."
              value={trustScoreTextData.bio}
              onChange={(e) => setTrustScoreTextData(prev => ({ ...prev, bio: e.target.value }))}
              rows={3}
            />
          </div>

          <div className="text-input-group">
            <label className="input-label">Prompts & Answers (Optional)</label>
            {trustScoreTextData.prompts.map((prompt, index) => (
              <div key={index} className="prompt-item">
                <input
                  type="text"
                  className="text-input"
                  placeholder="Question"
                  value={prompt.question}
                  onChange={(e) => setTrustScoreTextData(prev => ({
                    ...prev,
                    prompts: prev.prompts.map((p, i) => 
                      i === index ? { ...p, question: e.target.value } : p
                    )
                  }))}
                />
                <input
                  type="text"
                  className="text-input"
                  placeholder="Answer"
                  value={prompt.answer}
                  onChange={(e) => setTrustScoreTextData(prev => ({
                    ...prev,
                    prompts: prev.prompts.map((p, i) => 
                      i === index ? { ...p, answer: e.target.value } : p
                    )
                  }))}
                />
                {trustScoreTextData.prompts.length > 1 && (
                  <button
                    className="remove-prompt-btn"
                    onClick={() => setTrustScoreTextData(prev => ({
                      ...prev,
                      prompts: prev.prompts.filter((_, i) => i !== index)
                    }))}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            <button
              className="add-prompt-btn"
              onClick={() => setTrustScoreTextData(prev => ({
                ...prev,
                prompts: [...prev.prompts, { question: '', answer: '' }]
              }))}
            >
              <Plus size={16} />
              Add Prompt
            </button>
          </div>

          <div className="text-input-group">
            <label className="input-label">Metadata (Optional)</label>
            <textarea
              className="text-input"
              placeholder="Enter metadata (e.g., IP=192.168.0.3, device=Android, region=CA)..."
              value={trustScoreTextData.metadata}
              onChange={(e) => setTrustScoreTextData(prev => ({ ...prev, metadata: e.target.value }))}
              rows={4}
            />
          </div>

          <div className="text-input-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={trustScoreTextData.image_block}
                onChange={(e) => setTrustScoreTextData(prev => ({ ...prev, image_block: e.target.checked }))}
              />
              <span className="checkmark"></span>
              <span>Image Block (if enabled, returns 0 trust score if any image check fails)</span>
            </label>
          </div>
        </div>
      )}

      {/* Test Controls */}
      <div className="test-controls">
        <div className="controls-header">
          <h3 className="section-title">Run Test</h3>
        </div>

        <div className="test-button-container">
                <button
            className={`test-button ${currentMode?.id || ''}`}
            onClick={runTest}
            disabled={isLoading || isLoadingModeration || (
              (testMode === 'single' || testMode === 'ai-detect' || testMode === 'composite-check') && !selectedImage
            ) || (
              (testMode === 'batch' || testMode === 'trust-score') && selectedImages.length === 0
            ) || (
              testMode === 'text-analysis' && !textAnalysisData.bio && 
              textAnalysisData.prompts.every(p => !p.question && !p.answer) && 
              !textAnalysisData.metadata
            ) || (
              testMode === 'face-verify' && (!selfieImage || profileImages.length === 0)
            ) || (
              testMode === 'moderation' && (moderationImages.length === 0 || !selectedApiKey)
            )}
            style={{ '--mode-color': currentMode?.color } as React.CSSProperties}
          >
            {(isLoading || isLoadingModeration) ? (
              <>
                <Loader className="button-icon spinning" />
                Testing...
                                      </>
                                    ) : (
              <>
                <Play className="button-icon" />
                Run {currentMode?.name} Test
              </>
            )}
          </button>
                                  </div>
                              </div>

      {/* Moderation Test Bins */}
      {testMode === 'moderation' && moderationProfiles.length > 0 && (
        <div className="moderation-bins-section">
          <div className="bins-header">
            <h3 className="section-title">Test Moderation Results</h3>
            <button 
              className="clear-bins-button"
              onClick={clearModerationBins}
            >
              <Trash2 size={16} />
              Clear All
            </button>
          </div>
          
          <div className="moderation-bins">
            {/* Approved Bin */}
            <div className="moderation-bin approved-bin">
              <div className="bin-header">
                <CheckCircle2 className="bin-icon approved" size={20} />
                <h4>Approved ({moderationProfiles.filter(p => p.status === 'approved').length})</h4>
              </div>
              <div className="bin-profiles">
                {moderationProfiles.filter(p => p.status === 'approved').map(profile => (
                  <ModerationProfileCardWrapper key={profile.id} profile={profile} />
                ))}
                {moderationProfiles.filter(p => p.status === 'approved').length === 0 && (
                  <div className="empty-bin">No approved profiles</div>
                )}
              </div>
            </div>

            {/* Pending Bin */}
            <div className="moderation-bin pending-bin">
              <div className="bin-header">
                <Clock className="bin-icon pending" size={20} />
                <h4>Pending ({moderationProfiles.filter(p => p.status === 'pending').length})</h4>
              </div>
              <div className="bin-profiles">
                {moderationProfiles.filter(p => p.status === 'pending').map(profile => (
                  <ModerationProfileCardWrapper key={profile.id} profile={profile} />
                ))}
                {moderationProfiles.filter(p => p.status === 'pending').length === 0 && (
                  <div className="empty-bin">No pending profiles</div>
                )}
              </div>
            </div>

            {/* Rejected Bin */}
            <div className="moderation-bin rejected-bin">
              <div className="bin-header">
                <XCircle className="bin-icon rejected" size={20} />
                <h4>Rejected ({moderationProfiles.filter(p => p.status === 'rejected').length})</h4>
              </div>
              <div className="bin-profiles">
                {moderationProfiles.filter(p => p.status === 'rejected').map(profile => (
                  <ModerationProfileCardWrapper key={profile.id} profile={profile} />
                ))}
                {moderationProfiles.filter(p => p.status === 'rejected').length === 0 && (
                  <div className="empty-bin">No rejected profiles</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Results Section */}
      {(testResult || batchTestResult) && (
        <div className="results-section">
          <div className="results-header">
            <h3 className="section-title">Test Results</h3>
            <div className="result-meta">
              <span className="result-timestamp">
                <Clock size={14} />
                {new Date(testResult?.timestamp || batchTestResult?.timestamp || '').toLocaleString()}
              </span>
                          </div>
                        </div>

          {testResult && (
            <div className={`result-card ${testResult.success ? 'success' : 'error'}`}>
              <div className="result-header">
                <div className="result-status">
                  {testResult.success ? (
                    <CheckCircle className="status-icon success" />
                  ) : (
                    <XCircle className="status-icon error" />
                  )}
                  <span className="status-text">
                    {testResult.success ? 'Test Passed' : 'Test Failed'}
                      </span>
                    </div>
                  </div>

              {testResult.success && testResult.data ? (
                <div className="result-content">
                  {/* Face Verify Results */}
                  {testMode === 'face-verify' && testResult.data.verified !== undefined ? (
                    <div className="face-verify-results">
                      <div className="verify-status">
                        <div className={`verify-badge ${testResult.data.verified ? 'verified' : 'not-verified'}`}>
                          {testResult.data.verified ? (
                            <>
                              <CheckCircle className="status-icon success" size={24} />
                              <span>Verified</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="status-icon error" size={24} />
                              <span>Not Verified</span>
                            </>
                          )}
                        </div>
                        <div className="verify-details">
                          <div className="detail-item">
                            <span className="detail-label">Similarity Threshold:</span>
                            <span className="detail-value">{testResult.data.similarity_threshold || 90}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Matches Found:</span>
                            <span className="detail-value">{testResult.data.matches?.length || 0}</span>
                          </div>
                        </div>
                      </div>
                      
                      {testResult.data.best_match && (
                        <div className="best-match-section">
                          <h4>Best Match</h4>
                          <div className="match-card">
                            <div className="match-item">
                              <span className="match-label">Profile Index:</span>
                              <span className="match-value">{testResult.data.best_match.profile_index}</span>
                            </div>
                            <div className="match-item">
                              <span className="match-label">Similarity:</span>
                              <span className="match-value">{testResult.data.best_match.similarity}%</span>
                            </div>
                            <div className="match-item">
                              <span className="match-label">Confidence:</span>
                              <span className="match-value">{testResult.data.best_match.confidence}%</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {testResult.data.matches && testResult.data.matches.length > 0 && (
                        <div className="all-matches-section">
                          <h4>All Matches</h4>
                          <div className="matches-grid">
                            {testResult.data.matches.map((match: any, index: number) => (
                              <div key={index} className="match-card">
                                <div className="match-item">
                                  <span className="match-label">Profile Index:</span>
                                  <span className="match-value">{match.profile_index}</span>
                                </div>
                                <div className="match-item">
                                  <span className="match-label">Similarity:</span>
                                  <span className="match-value">{match.similarity}%</span>
                                </div>
                                <div className="match-item">
                                  <span className="match-label">Confidence:</span>
                                  <span className="match-value">{match.confidence}%</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="raw-json-section">
                        <details>
                          <summary>Raw JSON Response</summary>
                          <pre className="result-json">
                            {JSON.stringify(testResult.data, null, 2)}
                          </pre>
                        </details>
                      </div>
                    </div>
                  ) : (
                    <div className="result-data">
                      <pre className="result-json">
                        {JSON.stringify(testResult.data, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
                ) : (
                <div className="result-error">
                  <AlertCircle className="error-icon" />
                  <p className="error-message">{testResult.error}</p>
                  </div>
                )}
              </div>
          )}

                {batchTestResult && (
            <div className={`result-card ${batchTestResult.success ? 'success' : 'error'}`}>
                    <div className="result-header">
                <div className="result-status">
                  {batchTestResult.success ? (
                    <CheckCircle className="status-icon success" />
                  ) : (
                    <XCircle className="status-icon error" />
                  )}
                  <span className="status-text">
                    {batchTestResult.success ? 'Batch Test Passed' : 'Batch Test Failed'}
                  </span>
                      </div>
                    </div>

              {batchTestResult.success && batchTestResult.data ? (
                    <div className="result-content">
                      {/* Trust Score Results */}
                      {testMode === 'trust-score' && batchTestResult.data.trust_score !== undefined ? (
                        <div className="trust-score-results">
                          <div className="trust-score-display">
                            <div className="trust-score-value">
                              <span className="score-label">Trust Score:</span>
                              <span className="score-number">{batchTestResult.data.trust_score}</span>
                              <span className="score-max">/ 100</span>
                            </div>
                          </div>
                          
                          {batchTestResult.data.image_results && batchTestResult.data.image_results.length > 0 && (
                            <div className="image-results-section">
                              <h4>Image Results:</h4>
                              <div className="results-grid">
                                {batchTestResult.data.image_results.map((result: any, index: number) => (
                                  <div key={index} className={`individual-result ${result.passed ? 'success' : 'error'}`}>
                                    <div className="result-header">
                                      <span className="image-index">Image {index + 1}</span>
                                      {result.passed ? (
                                        <CheckCircle className="status-icon success" size={16} />
                                      ) : (
                                        <XCircle className="status-icon error" size={16} />
                                      )}
                                    </div>
                                    <div className="result-details">
                                      {result.face_detect !== null && result.face_detect !== undefined && (
                                        <div className="detail-item">
                                          <span className="detail-label">Face Detected:</span>
                                          <span className="detail-value">{result.face_detect.faceDetected ? 'Yes' : 'No'}</span>
                                        </div>
                                      )}
                                      {result.ai_image_detect !== null && result.ai_image_detect !== undefined && (
                                        <div className="detail-item">
                                          <span className="detail-label">AI Generated:</span>
                                          <span className="detail-value">{result.ai_image_detect.aiGenerated ? 'Yes' : 'No'}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {batchTestResult.data.text_results && (
                            <div className="text-results-section">
                              <h4>Text Analysis Results:</h4>
                              <div className="text-result-item">
                                <span className="text-label">Score:</span>
                                <span className="text-value">{batchTestResult.data.text_results.score}</span>
                              </div>
                              {batchTestResult.data.text_results.error && (
                                <div className="text-error">
                                  <AlertCircle className="error-icon" size={14} />
                                  <span>{batchTestResult.data.text_results.error}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Batch Face Detection Results */
                        <>
                          <div className="batch-summary">
                            <div className="summary-item">
                              <span className="summary-label">Total Images:</span>
                              <span className="summary-value">{batchTestResult.data.totalImages || batchTestResult.data.results?.length || 0}</span>
                            </div>
                            {batchTestResult.data.totalFaces !== undefined && (
                              <div className="summary-item">
                                <span className="summary-label">Total Faces:</span>
                                <span className="summary-value">{batchTestResult.data.totalFaces}</span>
                              </div>
                            )}
                            {batchTestResult.data.processingTimeMs !== undefined && (
                              <div className="summary-item">
                                <span className="summary-label">Processing Time:</span>
                                <span className="summary-value">{batchTestResult.data.processingTimeMs}ms</span>
                              </div>
                            )}
                          </div>
                          
                          {batchTestResult.data.results && batchTestResult.data.results.length > 0 && (
                            <div className="batch-results">
                              <h4>Individual Results:</h4>
                              <div className="results-grid">
                                {batchTestResult.data.results.map((result: any, index: number) => (
                        <div key={index} className={`individual-result ${result.success ? 'success' : 'error'}`}>
                          <div className="result-header">
                            <span className="image-index">Image {result.imageIndex + 1}</span>
                            {result.success ? (
                              <CheckCircle className="status-icon success" size={16} />
                            ) : (
                              <XCircle className="status-icon error" size={16} />
                            )}
                                </div>
                                {result.success ? (
                                  <div className="result-details">
                              <div className="detail-item">
                                <span>Faces Detected:</span>
                                <span>{result.faceCount}</span>
                              </div>
                              <div className="detail-item">
                                <span>Confidence:</span>
                                <span>{(result.confidence * 100).toFixed(1)}%</span>
                              </div>
                                  </div>
                                ) : (
                                  <div className="error-details">
                                    <span className="error-text">{result.error}</span>
                                  </div>
                                )}
                              </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                      ) : (
                <div className="result-error">
                  <AlertCircle className="error-icon" />
                          <p className="error-message">{batchTestResult.error}</p>
                        </div>
                      )}
                  </div>
                )}
          </div>
        )}
    </div>
  );
};

export default TestingPage;